"use client";
import { Link } from "@/i18n/routing";
import { LinkProps } from "next/link";
import { useRouter } from "@/i18n/routing";
import React, { useState } from "react";
import { Button, buttonVariants } from "../ui/button";
import { Drawer, DrawerContent, DrawerTrigger } from "../ui/drawer";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import LocaleSwitcher from "../localization/locale-switcher";
import { navigationConfig } from "@/config/navigation";

export default function MobileNavigation() {
  const updatedNavigationConfig = {
    ...navigationConfig,
    main: [
      {
        key: "home",
        titleKey: "header.navigation.items.home.title",
        href: "/",
        type: "link",
      },
      ...navigationConfig.main,
      {
        key: "use_cases",
        titleKey: "header.navigation.items.use_cases.title",
        href: "/use-cases",
        type: "link",
      },
    ],
  };

  const [open, setOpen] = useState(false);
  const t = useTranslations();

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-full gap-4 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 lg:hidden"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="!size-6"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9h16.5m-16.5 6.75h16.5" />
          </svg>
          <span className="sr-only">Toggle Menu</span>
        </Button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[80svh] p-0">
        <div className="space-y-6 overflow-auto p-6">
          {/* Main navigation */}
          <div className="flex flex-col space-y-3">
            <span className="scroll-m-20 text-2xl font-semibold tracking-tight">Navigation</span>
            {updatedNavigationConfig.main.map((item) =>
              item.type === "link" ? (
                <MobileLink key={item.key} href={item.href} onOpenChange={setOpen}>
                  {t(item.titleKey)}
                </MobileLink>
              ) : null,
            )}
          </div>

          {/* Proxies / Pricing */}
          <div className="flex flex-col space-y-2">
            <span className="scroll-m-20 text-2xl font-semibold tracking-tight">
              {t(navigationConfig.proxiesGroup.titleKey)}
            </span>
            {navigationConfig.proxiesGroup.items.map((item) => (
              <MobileLink key={item.href} href={item.href} onOpenChange={setOpen}>
                {t(item.titleKey)}
              </MobileLink>
            ))}
          </div>

          {/* Proxies / Pricing */}
          <div className="flex flex-col space-y-2">
            <span className="scroll-m-20 text-2xl font-semibold tracking-tight">
              {t(navigationConfig.freeToolsGroup.titleKey)}
            </span>
            {navigationConfig.freeToolsGroup.items.map((item) => (
              <MobileLink key={item.href} href={item.href} onOpenChange={setOpen}>
                {t(item.titleKey)}
              </MobileLink>
            ))}
          </div>

          {/* Actions */}
          <div className="space-y-6">
            <Link
              className={cn(buttonVariants({ size: "sm" }), "w-full")}
              href="https://dashboard.thunderproxy.com/auth/register"
            >
              Sign up for a new account
            </Link>
            <Link
              className={cn(buttonVariants({ variant: "outline", size: "sm" }), "w-full")}
              href="https://dashboard.thunderproxy.com/auth/register"
            >
              Log in to an existing account
            </Link>
            <div className="space-y-3">
              <span className="scroll-m-20 text-lg font-semibold tracking-tight">Language</span>
              <div className="max-w-[160px]">
                <LocaleSwitcher />
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface MobileLinkProps extends LinkProps {
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
}

function MobileLink({
  href,
  onOpenChange,
  className,
  children,
  locale,
  ...props
}: MobileLinkProps & { locale?: string | false }) {
  const router = useRouter();

  return (
    <Link
      href={href}
      onClick={() => {
        router.push(href.toString());
        onOpenChange?.(false);
      }}
      className={cn("text-[1.15rem]", className)}
      {...(locale !== false ? { locale } : {})}
      {...props}
    >
      {children}
    </Link>
  );
}
